import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Switch,
  Button,
  Paper,
  Divider,
  Chip,
  Tooltip,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Toast from "./Toast";

const MyDailyDevotionSettings = ({ organization }) => {
  const [devotionDefault, setDevotionDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const API_URL = process.env.REACT_APP_API_URL || "http://45.194.3.128:4000/api/admin/";

  useEffect(() => {
    const fetchDevotionSettings = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}devotion-list`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ search: "", organization: organization || null, page: 1 }),
        });
        const data = await response.json();
        if (response.ok && data.settings) {
          setDevotionDefault(data.settings.hdailydevotiondefault === "yes");
        }
      } catch (err) {
        console.error("Error fetching devotion settings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDevotionSettings();
  }, [organization]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}update-devotion-settings`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ devotionType: devotionDefault }),
      });
      const data = await response.json();
      if (!response.ok || data.success === false) {
        if (data.message === "Invalid token") {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return;
        }
        setToast({ open: true, message: data.message || "Failed to save settings", severity: "error" });
        return;
      }
      setToast({ open: true, message: "Daily Devotion settings saved successfully!", severity: "success" });
    } catch (err) {
      setToast({ open: true, message: "Network error. Please try again.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ background: "linear-gradient(135deg, rgba(23,115,115,0.18) 0%, rgba(37,105,116,0.15) 100%)", border: "1px solid rgba(76,161,175,0.35)", borderRadius: 3, p: { xs: 2.5, md: 3.5 }, mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: 2, backgroundColor: "rgba(76,161,175,0.2)", border: "1px solid rgba(76,161,175,0.4)", display: "flex", alignItems: "center", justifyContent: "center", color: "#4CA1AF", flexShrink: 0 }}>
          <MenuBookIcon sx={{ fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: "#FFFFFF", letterSpacing: 0.3 }}>My Daily Devotion</Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", mt: 0.3 }}>Control whether the default GPN devotion stream or your custom org devotion is shown to mobile users.</Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, p: { xs: 2.5, md: 3 }, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#FFFFFF" }}>Default Daily Devotion</Typography>
              <Chip size="small" label={devotionDefault ? "ON" : "OFF"} sx={{ bgcolor: devotionDefault ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)", color: devotionDefault ? "#10B981" : "#EF4444", border: `1px solid ${devotionDefault ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)"}`, fontWeight: 700, fontSize: "0.7rem", height: 22 }} />
              <Tooltip title="When ON, users see the GPN default devotion content. When OFF, users see your organization custom devotion content.">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.4)", cursor: "help" }} />
              </Tooltip>
            </Box>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem" }}>{devotionDefault ? "Mobile users currently see the GPN default devotion stream." : "Mobile users currently see your organization custom devotion content."}</Typography>
          </Box>
          <Switch checked={devotionDefault} onChange={(e) => setDevotionDefault(e.target.checked)} disabled={loading} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4CA1AF" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4CA1AF" }, transform: "scale(1.15)" }} />
        </Box>

        <Divider sx={{ my: 2.5, borderColor: "rgba(255,255,255,0.08)" }} />

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <Box sx={{ p: 2, borderRadius: 2, background: devotionDefault ? "linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(5,150,105,0.08) 100%)" : "rgba(255,255,255,0.03)", border: `1px solid ${devotionDefault ? "rgba(16,185,129,0.3)" : "rgba(255,255,255,0.08)"}`, transition: "all 0.3s ease" }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {devotionDefault ? <CheckCircleOutlineIcon sx={{ color: "#10B981", fontSize: 18 }} /> : <RadioButtonUncheckedIcon sx={{ color: "rgba(255,255,255,0.35)", fontSize: 18 }} />}
              <Typography variant="body2" fontWeight={700} sx={{ color: devotionDefault ? "#10B981" : "rgba(255,255,255,0.5)" }}>Default ON (Recommended)</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: devotionDefault ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>Users see GPN curated devotion stream. Best for organizations that do not have custom content yet.</Typography>
          </Box>

          <Box sx={{ p: 2, borderRadius: 2, background: !devotionDefault ? "linear-gradient(135deg, rgba(76,161,175,0.12) 0%, rgba(37,105,116,0.08) 100%)" : "rgba(255,255,255,0.03)", border: `1px solid ${!devotionDefault ? "rgba(76,161,175,0.3)" : "rgba(255,255,255,0.08)"}`, transition: "all 0.3s ease" }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {!devotionDefault ? <CheckCircleOutlineIcon sx={{ color: "#4CA1AF", fontSize: 18 }} /> : <RadioButtonUncheckedIcon sx={{ color: "rgba(255,255,255,0.35)", fontSize: 18 }} />}
              <Typography variant="body2" fontWeight={700} sx={{ color: !devotionDefault ? "#4CA1AF" : "rgba(255,255,255,0.5)" }}>My Daily Devotion (Custom)</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: !devotionDefault ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>Users see devotions you upload in the Daily Devotions module. Full control over your content.</Typography>
          </Box>
        </Box>
      </Paper>

      <Box display="flex" justifyContent="flex-end" gap={1.5}>
        <Button onClick={() => window.location.reload()} sx={{ color: "rgba(255,255,255,0.6)", textTransform: "none", fontWeight: 500 }}>Reset</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading} sx={{ background: "linear-gradient(135deg, #177373 0%, #256974 100%)", color: "#FFFFFF", fontWeight: 700, textTransform: "none", px: 3, py: 1, borderRadius: 2, boxShadow: "0 4px 12px rgba(23,115,115,0.3)", "&:hover": { background: "linear-gradient(135deg, #1C8A8A 0%, #2f7e8a 100%)" } }}>
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </Box>

      <Toast toast={toast} setToast={setToast} />
    </Box>
  );
};

export default MyDailyDevotionSettings;
